/**
 * SVG Icon library — replaces all unicode emojis site-wide.
 * All icons use currentColor and are 1em × 1em by default.
 */

const BASE = {
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.75",
    strokeLinecap: "round",
    strokeLinejoin: "round",
};

// ── Document / Note ────────────────────────────────────────────
export function IconDocument({ size = "1em", style, className }) {
    return (
        <svg {...BASE} width={size} height={size} style={style} className={className}>
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
            <polyline points="10 9 9 9 8 9" />
        </svg>
    );
}

// ── User / Person ──────────────────────────────────────────────
export function IconUser({ size = "1em", style, className }) {
    return (
        <svg {...BASE} width={size} height={size} style={style} className={className}>
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
        </svg>
    );
}

// ── Pencil / Edit ──────────────────────────────────────────────
export function IconEdit({ size = "1em", style, className }) {
    return (
        <svg {...BASE} width={size} height={size} style={style} className={className}>
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
        </svg>
    );
}

// ── Trash / Delete ─────────────────────────────────────────────
export function IconTrash({ size = "1em", style, className }) {
    return (
        <svg {...BASE} width={size} height={size} style={style} className={className}>
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
            <path d="M10 11v6M14 11v6" />
            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
        </svg>
    );
}

// ── Eye / View ─────────────────────────────────────────────────
export function IconEye({ size = "1em", style, className }) {
    return (
        <svg {...BASE} width={size} height={size} style={style} className={className}>
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
        </svg>
    );
}

// ── Heart / Like ───────────────────────────────────────────────
export function IconHeart({ size = "1em", filled = false, style, className }) {
    return (
        <svg
            {...BASE}
            fill={filled ? "currentColor" : "none"}
            width={size}
            height={size}
            style={style}
            className={className}
        >
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
        </svg>
    );
}

// ── Message / Comment ──────────────────────────────────────────
export function IconMessage({ size = "1em", style, className }) {
    return (
        <svg {...BASE} width={size} height={size} style={style} className={className}>
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
    );
}

// ── Folder ─────────────────────────────────────────────────────
export function IconFolder({ size = "1em", style, className }) {
    return (
        <svg {...BASE} width={size} height={size} style={style} className={className}>
            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
        </svg>
    );
}

// ── Paperclip / Attach ─────────────────────────────────────────
export function IconPaperclip({ size = "1em", style, className }) {
    return (
        <svg {...BASE} width={size} height={size} style={style} className={className}>
            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
        </svg>
    );
}

// ── Camera / Photo ─────────────────────────────────────────────
export function IconCamera({ size = "1em", style, className }) {
    return (
        <svg {...BASE} width={size} height={size} style={style} className={className}>
            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
            <circle cx="12" cy="13" r="4" />
        </svg>
    );
}

// ── Clipboard / Copy ───────────────────────────────────────────
export function IconClipboard({ size = "1em", style, className }) {
    return (
        <svg {...BASE} width={size} height={size} style={style} className={className}>
            <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
            <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
        </svg>
    );
}

// ── Check / Tick ───────────────────────────────────────────────
export function IconCheck({ size = "1em", style, className }) {
    return (
        <svg {...BASE} width={size} height={size} style={style} className={className}>
            <polyline points="20 6 9 17 4 12" />
        </svg>
    );
}

// ── Star / Favorite ───────────────────────────────────────────
export function IconStar({ size = "1em", filled = false, style, className }) {
    return (
        <svg
            {...BASE}
            width={size}
            height={size}
            style={style}
            className={className}
            fill={filled ? "currentColor" : "none"}
        >
            <polygon points="12 2.8 15.2 8.6 21.7 9.2 16.9 13.4 18.4 19.8 12 16.4 5.6 19.8 7.1 13.4 2.3 9.2 8.8 8.6" />
        </svg>
    );
}

// ── X / Close ──────────────────────────────────────────────────
export function IconX({ size = "1em", style, className }) {
    return (
        <svg {...BASE} width={size} height={size} style={style} className={className}>
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
    );
}

// ── Plus / Add ─────────────────────────────────────────────────
export function IconPlus({ size = "1em", style, className }) {
    return (
        <svg {...BASE} width={size} height={size} style={style} className={className}>
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
    );
}

// ── External Link / Open ───────────────────────────────────────
export function IconExternalLink({ size = "1em", style, className }) {
    return (
        <svg {...BASE} width={size} height={size} style={style} className={className}>
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
            <polyline points="15 3 21 3 21 9" />
            <line x1="10" y1="14" x2="21" y2="3" />
        </svg>
    );
}

// ── Search ─────────────────────────────────────────────────────
export function IconSearch({ size = "1em", style, className }) {
    return (
        <svg {...BASE} width={size} height={size} style={style} className={className}>
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
    );
}

// ── LogOut ────────────────────────────────────────────────────
export function IconLogOut({ size = "1em", style, className }) {
    return (
        <svg {...BASE} width={size} height={size} style={style} className={className}>
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
        </svg>
    );
}

// ── Bell / Notifications ───────────────────────────────────────
export function IconBell({ size = "1em", style, className }) {
    return (
        <svg {...BASE} width={size} height={size} style={style} className={className}>
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
    );
}

// ── Pencil (simple) ───────────────────────────────────────────
export function IconPencil({ size = "1em", style, className }) {
    return (
        <svg {...BASE} width={size} height={size} style={style} className={className}>
            <path d="M12 20h9" />
            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
        </svg>
    );
}

// ── Sun (light mode) ──────────────────────────────────────────
export function IconSun({ size = "1em", style, className }) {
    return (
        <svg {...BASE} width={size} height={size} style={style} className={className}>
            <circle cx="12" cy="12" r="5" />
            <line x1="12" y1="1" x2="12" y2="3" />
            <line x1="12" y1="21" x2="12" y2="23" />
            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
            <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
            <line x1="1" y1="12" x2="3" y2="12" />
            <line x1="21" y1="12" x2="23" y2="12" />
            <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
            <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
        </svg>
    );
}

// ── Moon (dark mode) ──────────────────────────────────────────
export function IconMoon({ size = "1em", style, className }) {
    return (
        <svg {...BASE} width={size} height={size} style={style} className={className}>
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
        </svg>
    );
}

// ── Users / Community ─────────────────────────────────────────
export function IconUsers({ size = "1em", style, className }) {
    return (
        <svg {...BASE} width={size} height={size} style={style} className={className}>
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
    );
}

// ── Clock ─────────────────────────────────────────────────────
export function IconClock({ size = "1em", style, className }) {
    return (
        <svg {...BASE} width={size} height={size} style={style} className={className}>
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
        </svg>
    );
}
